import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@shared/components/ui/dialog";
import { Badge } from "@shared/components/ui/badge";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { Send, Check, X, Loader2, AlertCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import * as packagesService from "../services/packagesService";

export default function SpeedafOrderButton({
  packageData,
  onSuccess,
  variant = "outline",
  size = "sm",
  className = "",
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  // Check if Speedaf order already exists
  const existingSpeedafOrder = packageData?.externalOrders?.find(
    (order) => order.provider === "speedaf"
  );

  // Check if there's a failed order
  const hasFailedOrder = packageData?.externalOrders?.some(
    (order) => order.provider === "speedaf" && order.status === "error"
  );

  // Check if there's a successful order
  const hasSuccessfulOrder = packageData?.externalOrders?.some(
    (order) => order.provider === "speedaf" && order.status === "created"
  );

  const createOrderMutation = useMutation({
    mutationFn: () => packagesService.createSpeedafOrder(packageData._id),
    onSuccess: (data) => {
      toast.success(t("speedaf.orderCreatedSuccessfully"));
      setIsOpen(false);
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message ||
        error.message ||
        t("speedaf.orderCreationFailed");
      toast.error(message);
    },
  });

  const retryOrderMutation = useMutation({
    mutationFn: () => packagesService.retrySpeedafOrder(packageData._id),
    onSuccess: (data) => {
      toast.success(t("speedaf.orderRetrySuccessful"));
      setIsOpen(false);
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message ||
        error.message ||
        t("speedaf.orderRetryFailed");
      toast.error(message);
    },
  });

  const handleCreateOrder = () => {
    createOrderMutation.mutate();
  };

  const handleRetryOrder = () => {
    retryOrderMutation.mutate();
  };

  // If order already exists, show view button
  if (existingSpeedafOrder) {
    return (
      <Dialog>
        <DialogTrigger asChild>
          <Button variant={variant} size={size} className={className}>
            <Check className="h-4 w-4 mr-2" />
            {t("speedaf.viewOrder")}
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("speedaf.orderDetails")}</DialogTitle>
            <DialogDescription>
              {t("speedaf.speedafOrderInformation")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                {t("speedaf.status")}:
              </span>
              <Badge
                variant={
                  existingSpeedafOrder.status === "created"
                    ? "success"
                    : "destructive"
                }
              >
                {existingSpeedafOrder.status}
              </Badge>
            </div>

            {existingSpeedafOrder.status === "error" && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {t("speedaf.orderCreationFailed")}
                  {existingSpeedafOrder.rawResponse?.message && (
                    <div className="mt-1 text-xs">
                      {existingSpeedafOrder.rawResponse.message}
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {existingSpeedafOrder.providerOrderId && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                  {t("speedaf.billCode")}:
                </span>
                <code className="text-sm bg-muted px-2 py-1 rounded">
                  {existingSpeedafOrder.providerOrderId}
                </code>
              </div>
            )}

            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                {t("speedaf.createdAt")}:
              </span>
              <span className="text-sm text-muted-foreground">
                {new Date(existingSpeedafOrder.createdAt).toLocaleString()}
              </span>
            </div>

            {existingSpeedafOrder.rawResponse && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <details className="cursor-pointer">
                    <summary className="text-sm font-medium">
                      {t("speedaf.viewRawResponse")}
                    </summary>
                    <pre className="mt-2 text-xs overflow-auto max-h-40 bg-muted p-2 rounded">
                      {JSON.stringify(
                        existingSpeedafOrder.rawResponse,
                        null,
                        2
                      )}
                    </pre>
                  </details>
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            {hasFailedOrder && !hasSuccessfulOrder && (
              <Button
                onClick={handleRetryOrder}
                disabled={retryOrderMutation.isPending}
                variant="default"
              >
                {retryOrderMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t("speedaf.retrying")}
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    {t("speedaf.retryOrder")}
                  </>
                )}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setIsOpen(false)}
              disabled={retryOrderMutation.isPending}
            >
              {t("common.close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Show create button if no order exists
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant={variant} size={size} className={className}>
          <Send className="h-4 w-4 mr-2" />
          {t("speedaf.createOrder")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("speedaf.createSpeedafOrder")}</DialogTitle>
          <DialogDescription>
            {t("speedaf.confirmOrderCreation")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t("speedaf.orderWillBeCreated")}
            </AlertDescription>
          </Alert>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t("common.customer")}:
              </span>
              <span className="font-medium">{packageData.customer.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t("common.phone")}:
              </span>
              <span className="font-mono">{packageData.customer.phone}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("common.city")}:</span>
              <span>{packageData.customer.city}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t("common.amount")}:
              </span>
              <span className="font-bold">{packageData.totalAmount} MAD</span>
            </div>
            {packageData.products?.length > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t("common.items")}:
                </span>
                <span>{packageData.products.length}</span>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setIsOpen(false)}
            disabled={createOrderMutation.isPending}
          >
            <X className="h-4 w-4 mr-2" />
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleCreateOrder}
            disabled={createOrderMutation.isPending}
          >
            {createOrderMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t("speedaf.creating")}
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                {t("speedaf.createOrder")}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
